/**
 * The view model behind the review-and-run flow. **Not the wire shape** — that is
 * `PayrollRunDetail` in `services/payroll/api/generated/v1/model`, and `usePayrollRun.adapter.ts`
 * maps one onto the other.
 *
 * The two are kept apart deliberately. The wire hangs every figure off `PayrollRunItem.lines[]` as
 * a `{kind, party, code, amount, hours}` row, and the four tabs each want a flat per-category
 * number; doing that split once here beats doing it in twenty column renderers. It is also what
 * let the screen survive the wire shape changing underneath it.
 *
 * Money is a decimal **string**, dates are `YYYY-MM-DD`, datetimes ISO 8601. Money is
 * `string | null` wherever the UI renders it: `'0.00'` is a real value and must render as `$0.00`,
 * `null` means we could not load it. A member cannot tell "nothing was withheld" from "we failed to
 * fetch it" if both come out as zero.
 */

import type {
    PayrollBlocker,
    PayrollLine,
    PayrollRunDetail,
    PayrollTotals,
} from 'services/payroll/api/generated/v1/model';

export type { PayrollBlocker, PayrollLine };

/**
 * The eight run-level sums, exactly as the wire sends them. The totals row renders these rather
 * than adding up the rows, so the table can never disagree with the provider's arithmetic.
 */
export type PayrollRunTotals = PayrollTotals;

/**
 * The line codes Collective owns, for `earning` and `time_off` lines only (`EarningCode` on the
 * backend). Tax and benefit codes stay vendor-supplied. Anything the provider sends that is not
 * one of these arrives as `other` — a salaried worker's base pay does, which is why "additional
 * earnings" is decided by code and not by "an earning with no hours".
 */
export const PAYROLL_LINE_CODES = {
    regularHours: 'regular_hours',
    overtime: 'overtime',
    doubleOvertime: 'double_overtime',
    pto: 'pto',
    sick: 'sick',
    bonus: 'bonus',
    cashTips: 'cash_tips',
    commission: 'commission',
    correction: 'correction',
    paycheckTips: 'paycheck_tips',
} as const;

/** One earning or time-off line, as the Hours worked tab and the pay-edit form read it. */
export interface HourLine {
    /** One of `PAYROLL_LINE_CODES`, or `other`. Rows are matched by this, never by position. */
    code: string;
    label: string;
    hours: string | null;
    amount: string | null;
}

export interface PayrollRunEmployee {
    /** What a line edit addresses. Rows are keyed by this, never by array position. */
    item_uuid: string;
    /** The join key onto the roster. */
    worker_uuid: string;
    full_name: string;
    job_title: string;
    is_owner: boolean;
    /** Left out of this run deliberately. Design groups these below the totals row. */
    excluded: boolean;
    payment_method: string;
    gross_pay: string | null;
    net_pay: string | null;
    company_paid_taxes: string | null;
    company_paid_benefits: string | null;
    /** Gross plus everything the employer pays on top. Summed here; the wire has no such field. */
    company_paid_total_amount: string | null;
    employee_paid_taxes: string | null;
    employee_benefit_contributions: string | null;
    employee_deductions: string | null;
    reimbursements: string | null;
    /** Kind `earning` lines that carry hours — regular, overtime, double overtime. */
    hours: HourLine[];
    /** Kind `time_off`. `null` when the payload carried none — distinct from an empty policy list. */
    time_off: HourLine[] | null;
    /** The coded extras — bonus, tips, commission, correction. Never the base pay line. */
    additional_earnings: string | null;
    /**
     * What the worker themselves pays in tax and benefit contributions, and the two sides of tax
     * added together. Summed in the adapter rather than in the column that shows them: a category
     * that is merely absent must add nothing while one we could not settle must make the whole
     * figure unknown, and by the time a column sees `null` those two are indistinguishable.
     */
    employee_taxes_and_benefits: string | null;
    tax_debit: string | null;
    /**
     * The wire's own lines, untouched. The pay-edit form reads and rewrites these; every flat field
     * above is a projection of them, re-derived through `withLines` when they change.
     */
    lines: PayrollLine[];
    /**
     * What this worker is paid on their standing terms, expressed per *this run's* pay period.
     * `null` whenever it cannot be derived honestly — see `usePayrollRun.adapter.ts`. Never a
     * fallback figure: a wrong baseline flags every row and trains the member to ignore the flag.
     *
     * Derived on the client only because the wire has no such field. If backend adds one, this
     * field survives and `applyStandingRates` disappears.
     */
    standing_gross_pay: string | null;
    /** `gross_pay` diverges from `standing_gross_pay` by more than the tolerance. */
    is_anomalous: boolean;
    /** The rate as design renders it under the name, e.g. `$98,000/yr`. `null` when unknown. */
    standing_rate_label: string | null;
    /**
     * The roster's FLSA classification, lower-case and underscored (`nonexempt`, `exempt`, …). It
     * decides which hourly pay types the provider accepts for this worker. `null` when the roster
     * has no match or reports none — unknown, not "exempt".
     */
    flsa_status: string | null;
    /** The roster's hourly rate, for estimating an hours edit. `null` unless the worker is paid by the hour. */
    hourly_rate: string | null;
}

/** `GET api/payroll/v1/payrolls/{payroll_uuid}/` — one payload, four tab projections. */
export interface PayrollRun {
    payroll_uuid: string;
    pay_schedule_uuid: string | null;
    /** How often this payroll recurs — `monthly`, `biweekly`, and so on. */
    frequency: string | null;
    /** Pay period. */
    start_date: string | null;
    end_date: string | null;
    /** Pay day. */
    check_date: string | null;
    /** When the funds leave the company's account. */
    debit_date: string | null;
    /** ISO 8601. A moment — 4:00pm PT — not a day. */
    payroll_deadline: string | null;
    off_cycle: boolean;
    processed: boolean;
    /** When the provider last recalculated these numbers. `null` before the first calculation. */
    calculated_at: string | null;
    /** Ours and normalized; `calculating` while a calculation is out at the provider. */
    status: PayrollRunDetail['status'];
    /** From the read, so a blocker shows before the member ever presses Submit. */
    blockers: PayrollBlocker[];
    /** Unprocessed and in a submittable status, late or not. Not "this will succeed". */
    can_submit: boolean;
    can_cancel: boolean;
    can_skip: boolean;
    can_prepare: boolean;
    /** The run that took this one's place after the provider deleted it; open that one instead. */
    replaced_by: string | null;
    employee_compensations: PayrollRunEmployee[];
    totals: PayrollRunTotals;
}
